import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api, { apiErr } from "../../lib/api";
import { ClipCard, toClip } from "./clip-utils";
import { toast } from "sonner";
import { useUploads } from "../../context/UploadContext";
import { Search, Loader2, Save, Plus, Video, Image as ImageIcon, Globe, Link2, ChevronLeft, ChevronRight, Layers, CheckSquare, Square } from "lucide-react";

const IMAGE_SOURCES = ["", "pexels", "pixabay", "unsplash", "openverse", "wikimedia", "met", "smithsonian", "wellcome", "archive"];
const VIDEO_SOURCES = ["", "pexels", "pixabay", "archive", "youtube"];
const SOURCE_LABEL_KEY = {
  "": "sourceAll", pexels: "sourcePexels", pixabay: "sourcePixabay", unsplash: "sourceUnsplash",
  openverse: "sourceOpenverse", wikimedia: "sourceWikimedia", archive: "sourceArchive", met: "sourceMet",
  smithsonian: "sourceSmithsonian", wellcome: "sourceWellcome", youtube: "sourceYoutube",
};

export default function PexelsBrowser({ projectId, keywords = [], characters = [], onSaved, onUse, replaceOnly = false, initialMedia, initialSource }) {
  const { t } = useTranslation();
  const { startCut, isCutting, getCut } = useUploads();
  // Al reemplazar un clip (ver initialMedia/initialSource, calculados en OneClickProducer a
  // partir de director_source/media del clip original) la búsqueda arranca ya en la pestaña y
  // fuente de las que vino — no tiene sentido ofrecer "vídeo, todas las fuentes" por defecto
  // cuando ya se sabe exactamente de dónde salió.
  const [media, setMedia] = useState(initialMedia || "video");
  const [query, setQuery] = useState(keywords[0] || "documental");
  const [source, setSource] = useState(initialSource || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [usingId, setUsingId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [youtubePages, setYoutubePages] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  // "Cortar" en un resultado de búsqueda TODAVÍA no tiene ficha en broll_files (es un resultado
  // efímero de la búsqueda) — hay que guardarlo primero (igual que "Guardar en Mi carpeta") y
  // recién entonces se puede cortar de verdad. cuttingSavingId cubre ese primer tramo (guardando,
  // antes de que exista un id real); cutFileId mapea el id del resultado -> id real ya guardado,
  // para poder seguir preguntando isCutting/getCut (que van por el id real) desde la misma
  // tarjeta aunque esté indexada por el id efímero del resultado.
  const [cuttingSavingId, setCuttingSavingId] = useState(null);
  const [cutFileId, setCutFileId] = useState({});

  const search = async (q = query, m = media, p = 1, src = source) => {
    setSearching(true);
    try {
      // "snapany" es una pestaña de tipo de medio propia en esta UI, pero para el backend sigue
      // siendo media="video" con source="snapany" (ver broll_search) — se traduce aquí para no
      // tener que enseñarle el concepto "snapany" a BrollSearchReq.media.
      const apiMedia = m === "snapany" ? "video" : m;
      const apiSource = m === "snapany" ? "snapany" : src;
      const { data } = await api.post(`/projects/${projectId}/broll/search`, { query: q, media: apiMedia, page: p, source: apiSource || undefined, page_token: apiSource === "youtube" && p > 1 ? youtubePages[p] : undefined });
      if (apiSource === "youtube") {
        setYoutubePages((prev) => ({ ...(p === 1 ? {} : prev), [p + 1]: data.next_page_token }));
      }
      setResults(data.results);
      setPage(data.page || p);
      setHasMore(!!data.has_more);
      setSelected(new Set());
    } catch (e) { toast.error(apiErr(e)); } finally { setSearching(false); }
  };

  // Busca sola al montar, con la query/pestaña/fuente que ya trae inicializadas (ver
  // initialMedia/initialSource más arriba) — antes abrir este panel solo dejaba los campos
  // rellenos y había que pulsar buscar o Enter a mano, aunque ya se supiera exactamente qué
  // buscar (p.ej. al reemplazar un clip, ver OneClickProducer.jsx). "snapany" se queda fuera:
  // no busca por palabra clave, resuelve la URL exacta que se pegue, así que no hay nada que
  // disparar solo con abrir.
  useEffect(() => {
    if (media !== "snapany") search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Descarga y guarda el resultado en la biblioteca del proyecto — paso común a "Guardar en
  // carpeta" y "Añadir a escena" (esta última guarda igual, solo que además usa el resultado
  // al instante en vez de dejarlo esperando en la carpeta).
  const persistToFolder = async (clip) => {
    const { data } = await api.post(`/projects/${projectId}/broll/library/save`, {
      title: clip.title, duration: clip.duration, thumbnail: clip.thumbnail, media: clip.media,
      download_url: clip.download_url, pexels_id: clip.pexels_id, source: clip.source,
      license: clip.license, attribution_required: clip.attribution_required,
      author: clip.author, source_url: clip.source_url,
      channel_id: clip.channel_id, video_id: clip.video_id,
      // Texto de búsqueda que encontró este resultado (todas las funciones de /broll/search
      // devuelven "query" en cada item) — se guarda para conservar la trazabilidad al
      // reemplazar un clip desde la revisión (ver replaceClip en OneClickProducer.jsx).
      query: clip.query,
      // Solo lo manda SnapAny: cabeceras extra que hace falta enviar para poder descargar
      // download_url (algunas plataformas exigen Referer/User-Agent/cookies concretos).
      extra_headers: clip.extra_headers,
    });
    onSaved && onSaved();
    return data;
  };

  const save = async (clip) => {
    setSavingId(clip.id);
    try {
      await persistToFolder(clip);
      toast.success(t("pex.savedOk", { title: clip.title }));
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingId(null); }
  };

  const addToScene = async (clip) => {
    setUsingId(clip.id);
    try {
      const doc = await persistToFolder(clip);
      // onUse (fillWith en TabBroll, replaceClip en OneClickProducer) ya muestra su propio
      // toast de éxito al terminar — no duplicarlo aquí.
      await onUse([toClip(doc)], clip.title);
    } catch (e) { toast.error(apiErr(e)); } finally { setUsingId(null); }
  };

  // Igual que el botón "Cortar" de Mi carpeta (mismo startCut del contexto global — misma
  // píldora de progreso, mismo endpoint), pero como el resultado de búsqueda aún no existe en
  // broll_files hay que guardarlo primero.
  const cutClip = async (clip) => {
    setCuttingSavingId(clip.id);
    try {
      const doc = await persistToFolder(clip);
      setCutFileId((m) => ({ ...m, [clip.id]: doc.id }));
      await startCut(projectId, doc.id, doc.title);
    } catch (e) { toast.error(apiErr(e)); } finally { setCuttingSavingId(null); }
  };

  const saveMany = async (clips) => {
    if (!clips.length || bulkSaving) return;
    setBulkSaving(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/broll/library/save-batch`, {
        items: clips.map((c) => ({
          title: c.title, duration: c.duration, thumbnail: c.thumbnail, media: c.media,
          download_url: c.download_url, pexels_id: c.pexels_id, source: c.source,
          license: c.license, attribution_required: c.attribution_required,
          author: c.author, source_url: c.source_url, extra_headers: c.extra_headers,
          channel_id: c.channel_id, video_id: c.video_id, query: c.query,
        })),
      });
      const savedCount = data.saved?.length || 0;
      const failedCount = data.failed?.length || 0;
      if (savedCount) toast.success(t("pex.batchSavedOk", { count: savedCount }));
      if (failedCount) toast.error(t("pex.batchSavedPartial", { count: failedCount }));
      setSelected(new Set());
      onSaved && onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBulkSaving(false); }
  };

  const switchMedia = (m) => {
    // El texto de búsqueda se mantiene al cambiar de pestaña (vídeo/imagen/web/SnapAny) — antes
    // se borraba (setQuery("")) y el usuario tenía que volver a escribirlo cada vez que quería
    // probar la misma búsqueda en otra fuente.
    setMedia(m); setResults([]); setSelected(new Set()); setPage(1); setHasMore(false);
    setSource((prev) => {
      const list = m === "image" ? IMAGE_SOURCES : m === "video" ? VIDEO_SOURCES : [];
      return list.includes(prev) ? prev : "";
    });
  };
  const sourceOptions = media === "image" ? IMAGE_SOURCES : media === "video" ? VIDEO_SOURCES : [""];
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // SnapAny es una pestaña propia (como Videos/Imágenes/Web), no una fuente dentro de "Videos de
  // stock": no busca por palabra clave, resuelve la URL exacta que se pegue (ver
  // _snapany_extract_post en el backend) — sin chips de sugerencia ni desplegable de fuente.
  const isSnapAny = media === "snapany";
  const chips = isSnapAny ? [] : media === "character" ? characters : keywords;
  const kind = media === "character" ? t("pex.searchWeb") : media === "image" ? t("pex.searchImages") : t("pex.searchVideos");
  const placeholder = isSnapAny ? t("pex.phSnapAny") : media === "character" ? t("pex.phWeb") : media === "image" ? t("pex.phImage") : t("pex.phVideo");
  const selectedClips = results.filter((r) => selected.has(r.id));
  const allSelected = results.length > 0 && selected.size === results.length;

  return (
    <div className="flex flex-col">
      <div className="flex gap-2 bg-slate-100 p-1.5 rounded-xl mb-4 w-fit flex-wrap" data-testid="pexels-media-toggle">
        <button data-testid="pexels-media-video" onClick={() => switchMedia("video")} className={`min-h-[44px] px-4 rounded-lg text-base font-bold flex items-center gap-2 transition-colors ${media === "video" ? "bg-white text-blue-700 shadow" : "text-slate-600"}`}><Video size={18} /> {t("pex.videos")}</button>
        <button data-testid="pexels-media-image" onClick={() => switchMedia("image")} className={`min-h-[44px] px-4 rounded-lg text-base font-bold flex items-center gap-2 transition-colors ${media === "image" ? "bg-white text-blue-700 shadow" : "text-slate-600"}`}><ImageIcon size={18} /> {t("pex.images")}</button>
        <button data-testid="pexels-media-web" onClick={() => switchMedia("character")} className={`min-h-[44px] px-4 rounded-lg text-base font-bold flex items-center gap-2 transition-colors ${media === "character" ? "bg-white text-blue-700 shadow" : "text-slate-600"}`}><Globe size={18} /> {t("pex.web")}</button>
        <button data-testid="pexels-media-snapany" onClick={() => switchMedia("snapany")} className={`min-h-[44px] px-4 rounded-lg text-base font-bold flex items-center gap-2 transition-colors ${media === "snapany" ? "bg-white text-blue-700 shadow" : "text-slate-600"}`}><Link2 size={18} /> {t("pex.snapany")}</button>
      </div>

      {media === "character" && (
        <p className="text-sm text-slate-500 mb-3">{t("pex.webNote")}</p>
      )}

      {media !== "character" && media !== "snapany" && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-3">
          <label className="text-sm font-semibold text-slate-600 shrink-0">{t("pex.sourceLabel")}</label>
          <select
            data-testid="broll-source-select"
            value={source}
            onChange={(e) => { setSource(e.target.value); search(query, media, 1, e.target.value); }}
            className="w-full min-w-0 truncate sm:w-auto sm:max-w-xs border-2 border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold focus:border-blue-600 outline-none"
          >
            {sourceOptions.map((s) => (
              <option key={s || "all"} value={s}>{t(`pex.${SOURCE_LABEL_KEY[s]}`)}</option>
            ))}
          </select>
        </div>
      )}

      {isSnapAny && (
        <p className="text-sm text-slate-500 mb-3">{t("pex.snapanyNote")}</p>
      )}

      <div className="flex gap-2 mb-3">
        <input data-testid="broll-search-input" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search(query, media, 1)} className="flex-1 min-w-0 border-2 border-slate-300 rounded-lg px-4 py-3 text-lg focus:border-blue-600 focus:ring-4 focus:ring-blue-600/20 outline-none" placeholder={placeholder} />
        <button data-testid="broll-search-button" onClick={() => search(query, media, 1)} disabled={searching} className="min-h-[56px] px-6 rounded-xl bg-blue-600 text-white hover:bg-blue-700 font-bold flex items-center justify-center disabled:opacity-50">
          {searching ? <Loader2 className="animate-spin" size={22} /> : <Search size={22} />}
        </button>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {chips.slice(0, 10).map((k) => (
            <button key={k} onClick={() => { setQuery(k); search(k, media, 1); }} className="bg-slate-100 hover:bg-blue-100 text-slate-700 text-sm font-semibold px-3 py-1.5 rounded-full">{media === "character" ? "🔍 " : "#"}{k}</button>
          ))}
        </div>
      )}

      <div className="relative overflow-y-auto vb-scroll" style={{ maxHeight: 420 }}>
        {searching && (
          <div className="absolute inset-0 z-10 bg-white/60 flex items-center justify-center rounded-lg">
            <Loader2 className="animate-spin text-blue-600" size={32} />
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3" data-testid="broll-results">
          {results.map((r) => {
            const savedId = cutFileId[r.id];
            return (
            <ClipCard key={r.id} clip={r}
              // En modo "reemplazar un clip" (revisión de vídeo, ver replaceOnly) solo tiene
              // sentido "añadir a escena" — pasa a ser el botón PRIMARIO y es el único que se
              // ofrece: "guardar en Mi carpeta" y "cortar" son gestión de biblioteca, no encajan
              // cuando lo único que se quiere es elegir un reemplazo y seguir.
              primaryLabel={replaceOnly ? (onUse ? t("mf.addToScene") : undefined) : t("pex.saveToFolder")}
              primaryIcon={replaceOnly ? Plus : Save}
              primaryTestId={replaceOnly ? `use-in-scene-${r.id}` : `save-to-folder-${r.id}`}
              onPrimary={replaceOnly ? (onUse ? () => addToScene(r) : undefined) : () => save(r)}
              primaryBusy={replaceOnly ? usingId === r.id : savingId === r.id}
              secondaryLabel={!replaceOnly && onUse ? t("mf.addToScene") : undefined} secondaryIcon={Plus}
              secondaryTestId={`use-in-scene-${r.id}`}
              onSecondary={!replaceOnly && onUse ? () => addToScene(r) : undefined} secondaryBusy={usingId === r.id}
              // Mismo botón "Cortar" que Mi carpeta, misma condición (kind video + >15s) — como
              // aquí no hay "kind" (es un resultado de búsqueda, no una ficha guardada), se usa
              // "media" que es su equivalente.
              canCut={!replaceOnly && r.media === "video" && (r.duration || 0) > 15}
              onCut={() => cutClip(r)}
              cutting={cuttingSavingId === r.id || (!!savedId && isCutting(savedId))}
              cutProgress={savedId ? getCut(savedId) : null}
              selectable selected={selected.has(r.id)} onToggleSelect={() => toggleSelect(r.id)} />
            );
          })}
          {results.length === 0 && !searching && <div className="col-span-full text-slate-500 text-base py-10 text-center">{t("pex.emptyHint", { kind })}</div>}
        </div>
      </div>

      {results.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {selected.size > 0 ? (
              <>
                <button data-testid="pexels-add-selected" onClick={() => saveMany(selectedClips)} disabled={bulkSaving}
                  className="min-h-[44px] px-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-bold flex items-center gap-1.5 disabled:opacity-60">
                  {bulkSaving ? <Loader2 className="animate-spin" size={16} /> : <CheckSquare size={16} />} {t("pex.addSelected", { count: selected.size })}
                </button>
                <button data-testid="pexels-clear-selection" onClick={() => setSelected(new Set())} className="min-h-[44px] px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-bold flex items-center gap-1.5">
                  <Square size={16} /> {t("pex.clearSelection")}
                </button>
              </>
            ) : (
              <button data-testid="pexels-add-all-page" onClick={() => saveMany(results)} disabled={bulkSaving}
                className="min-h-[44px] px-4 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-bold flex items-center gap-1.5 disabled:opacity-60">
                {bulkSaving ? <Loader2 className="animate-spin" size={16} /> : <Layers size={16} />} {t("pex.addAllPage", { count: results.length })}
              </button>
            )}
            {allSelected && <span className="text-xs text-slate-400">{t("pex.allOnPageSelected")}</span>}
          </div>

          <div className="flex items-center gap-2 shrink-0" data-testid="pexels-pagination">
            <button data-testid="pexels-prev-page" aria-label={t("pex.prevPage")} onClick={() => search(query, media, page - 1)} disabled={page <= 1 || searching}
              className="min-h-[40px] min-w-[40px] rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40 flex items-center justify-center text-slate-700">
              <ChevronLeft size={18} />
            </button>
            <span aria-live="polite" className="text-sm font-bold text-slate-700 min-w-[90px] text-center">{t("pex.page", { page })}</span>
            <button data-testid="pexels-next-page" aria-label={t("pex.nextPage")} onClick={() => search(query, media, page + 1)} disabled={!hasMore || searching}
              className="min-h-[40px] min-w-[40px] rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-40 flex items-center justify-center text-slate-700">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
